import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import styled from "@emotion/styled";

import { supportsFsHandles } from "@/utils/uploadPersistence";

import {
  UploadIcon,
  UploadIconBlock,
  FileItemContainer,
  DropTitleText,
  DropNormalText,
  DropTotalSizeText,
  FileList,
  FilePath,
  FileSize,
  DropHighlightedText,
  FileItemIcon,
  DeleteIcon,
  FileDelimiter,
} from "../../UploadForm.elements";
import { FormatSize } from "../../../Util/Utils.elements";

const TOTAL_SIZE_LIMIT_GB = 50;
const MAX_TOTAL_SIZE = TOTAL_SIZE_LIMIT_GB * 1024 * 1024 * 1024;

// Sidecar/metadata extensions that get auto-included by camera-export tools (Adobe Bridge,
// Lightroom, Capture One, etc.) but aren't clips. The accept="video/*" filter on the
// dropzone doesn't catch them because: (a) the fallback <input> below has no accept
// attribute (Safari/Firefox path), (b) showOpenFilePicker keeps the "All files" toggle
// open by design, and (c) .xmp often has an empty MIME on drag-drop so react-dropzone
// lets it through. Enforcing here means every entry path goes through the same filter.
const BLOCKED_EXTENSIONS = new Set([".xmp"]);

const getExtension = (name) => {
  if (!name) return "";
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return "";
  return name.slice(dot).toLowerCase();
};

const partitionByExtension = (files) => {
  const accepted = [];
  const rejected = [];
  for (const file of files) {
    if (BLOCKED_EXTENSIONS.has(getExtension(file?.name))) {
      rejected.push(file);
    } else {
      accepted.push(file);
    }
  }
  return { accepted, rejected };
};

const getColor = (props) => {
  if (props.isDragAccept) return "#00e676";
  if (props.isDragReject) return "#ff1744";
  if (props.isDragActive) return "#2196f3";
  return "#B2B2B2D9";
};

const DropContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px;
  border-width: 3px;
  border-radius: 8px;
  border-color: ${(props) => getColor(props)};
  border-style: dashed;
  background-color: #202c3d8c;
  color: #fff;
  outline: none;
  transition: border 0.24s ease-in-out;
  cursor: pointer;
`;

const DropErrorText = styled.div`
  margin-top: 10px;
  color: #ff4d4d;
  font-size: 14px;
  text-align: center;
`;

const calcTotalSize = (arr) => arr.reduce((sum, f) => sum + (f?.size || 0), 0);
const formatGB = (bytes) => (bytes / (1024 * 1024 * 1024)).toFixed(2);

export const Dropzone = ({
  files,
  setFiles,
  onTotalSizeChange,
  accept,
  inputTestId = "upload-file-input",
  dropzoneTestId = "upload-dropzone",
}) => {
  const totalSize = useMemo(() => calcTotalSize(files), [files]);
  const [localError, setLocalError] = useState("");
  const [rejectedExtError, setRejectedExtError] = useState("");

  useEffect(() => {
    const isOver = totalSize > MAX_TOTAL_SIZE;
    const errorText = isOver
        ? `Total size limit is ${TOTAL_SIZE_LIMIT_GB} GB. Current total: ${formatGB(
            totalSize
        )} GB.`
        : "";
    setLocalError(errorText);
    onTotalSizeChange?.({ totalSize, isOverLimit: isOver, errorText });
  }, [totalSize, onTotalSizeChange]);

  // react-dropzone's onDrop pipes files through `toFileWithPath`, which adds a non-writable
  // `path` getter. Files from showOpenFilePicker / the fallback <input> don't go through
  // that helper, so we attach `.path` here ourselves — keeps the file list rendering and
  // any downstream consumers consistent regardless of which entry point ran.
  const ensurePath = (file) => {
    if (file.path) return file;
    try {
      Object.defineProperty(file, "path", { value: file.name, configurable: true });
    } catch {
      // Some File implementations are frozen — render fallback handles those.
    }
    return file;
  };

  // Centralize the merge so showOpenFilePicker, fallback input, and drag-drop append the
  // same way. Extension filter runs here (not at the dropzone accept level) because two of
  // the three entry paths bypass that accept filter — see BLOCKED_EXTENSIONS above.
  const mergeNewFiles = useCallback((incoming) => {
    const { accepted, rejected } = partitionByExtension(incoming);
    if (rejected.length > 0) {
      const names = rejected.map((f) => f.name).join(", ");
      setRejectedExtError(
        `Skipped ${rejected.length} unsupported file${rejected.length === 1 ? "" : "s"} (.xmp sidecar files are not uploaded): ${names}`,
      );
    } else {
      setRejectedExtError("");
    }
    if (accepted.length === 0) return;
    setFiles((prevFiles) => {
      const existingFileNames = prevFiles.map((f) => f.name);
      const filteredNewFiles = accepted
        .filter((f) => !existingFileNames.includes(f.name))
        .map(ensurePath);
      return [...prevFiles, ...filteredNewFiles];
    });
  }, [setFiles]);

  // Drag-drop path: pull FileSystemFileHandle off each DataTransferItem on Chromium so dropped
  // files are resumable after a reload, same as showOpenFilePicker. Without this, drag-dropped
  // uploads silently fall back to the reselect card and only show Cancel after a refresh.
  //
  // react-dropzone calls this twice per drop: once on dragenter (for the accept/reject hover
  // preview) and once on drop. During dragenter, DataTransferItem.getAsFile() returns null —
  // items are locked until the actual drop — so for non-drop events we mirror react-dropzone's
  // default and return the raw DataTransferItem[] (its accept validator reads .type/.kind from
  // them). Only the drop event resolves to real Files with handles attached.
  //
  // DataTransferItemList is invalidated once the drop handler returns to the event loop, so
  // inside the .map we call getAsFile() and kick off getAsFileSystemHandle() synchronously per
  // item — capturing fallbackFile first means a rejected handle promise (or a non-Chromium
  // browser without getAsFileSystemHandle) still yields a usable File.
  const dropzoneGetFilesFromEvent = useCallback(async (event) => {
    if (event?.dataTransfer && event.type !== "drop") {
      const items = event.dataTransfer.items;
      return items
        ? Array.from(items).filter((item) => item.kind === "file")
        : [];
    }
    if (event?.target?.files) {
      return Array.from(event.target.files);
    }
    const items = event?.dataTransfer?.items
      ? Array.from(event.dataTransfer.items)
      : [];
    if (items.length === 0) {
      const files = event?.dataTransfer?.files;
      return files ? Array.from(files) : [];
    }
    // We keep the File from DataTransferItem.getAsFile() and only borrow the handle from
    // getAsFileSystemHandle() — attaching __fsHandle to the original File. The File from
    // handle.getFile() often has an empty .type on Chrome (e.g. .mov resolves to "" via
    // the handle but "video/quicktime" via DataTransfer), which then fails react-dropzone's
    // MIME-only accept check. We only need the handle for post-reload resume; the active
    // upload reads bytes from the File the user just dropped.
    const tasks = items
      .filter((item) => item.kind === "file")
      .map((item) => {
        const fallbackFile = item.getAsFile();
        let handlePromise = null;
        if (typeof item.getAsFileSystemHandle === "function") {
          try {
            handlePromise = item.getAsFileSystemHandle();
          } catch {
            handlePromise = null;
          }
        }
        if (!handlePromise || !fallbackFile) return Promise.resolve(fallbackFile);
        return handlePromise
          .then((handle) => {
            if (handle && handle.kind === "file") {
              try {
                fallbackFile.__fsHandle = handle;
              } catch {
                // Frozen File — resume just won't be available for this file. Drop-only
                // behavior, matches the pre-fix experience.
              }
            }
            return fallbackFile;
          })
          .catch(() => fallbackFile);
      });
    const resolved = await Promise.all(tasks);
    return resolved.filter(Boolean);
  }, []);

  // noClick + useFsAccessApi:false stop react-dropzone from owning the file picker. We drive
  // it ourselves so we can capture FileSystemFileHandle objects from showOpenFilePicker and
  // tag them onto each File — the ChunkedUploadService persists them for silent resume.
  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } =
      useDropzone({
        accept,
        noClick: true,
        useFsAccessApi: false,
        getFilesFromEvent: dropzoneGetFilesFromEvent,
        onDrop: (acceptedFiles) => {
          mergeNewFiles(acceptedFiles);
        },
      });

  const fallbackInputRef = useRef(null);

  // Convert react-dropzone's accept shape ({ "video/*": [] }) into showOpenFilePicker's
  // ({ description, accept: { "video/*": [".mp4"] } }). Picker rejects empty extension lists
  // in some Chromium builds, so we omit `accept` entirely when no extensions are configured.
  const pickerTypes = useMemo(() => {
    if (!accept || typeof accept !== "object") return undefined;
    const entries = Object.entries(accept).filter(([, exts]) => Array.isArray(exts));
    if (entries.length === 0) return undefined;
    const acceptMap = Object.fromEntries(entries);
    return [{ description: "Files", accept: acceptMap }];
  }, [accept]);

  const handleBrowseClick = useCallback(async () => {
    if (supportsFsHandles()) {
      try {
        const handles = await window.showOpenFilePicker({
          multiple: true,
          // excludeAcceptAllOption:false keeps the "All files" toggle so users aren't blocked
          // by an accept filter mismatch — the rest of the form already enforces type checks.
          excludeAcceptAllOption: false,
          ...(pickerTypes ? { types: pickerTypes } : {}),
        });
        const picked = await Promise.all(
          handles.map(async (h) => {
            const f = await h.getFile();
            // Expando — survives reference equality through dropzone's filter logic. Stripped
            // by the upload service after it's persisted to IndexedDB.
            f.__fsHandle = h;
            return f;
          })
        );
        mergeNewFiles(picked);
      } catch (err) {
        // AbortError = user dismissed picker; anything else falls back to the legacy input
        // so the user is never stuck without a way to add files.
        if (err && err.name !== "AbortError") {
          fallbackInputRef.current?.click();
        }
      }
      return;
    }
    fallbackInputRef.current?.click();
  }, [pickerTypes, mergeNewFiles]);

  const handleFallbackChange = (event) => {
    const list = event?.target?.files;
    if (!list || list.length === 0) return;
    mergeNewFiles(Array.from(list));
    if (fallbackInputRef.current) fallbackInputRef.current.value = "";
  };

  const handleDelete = (file) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f !== file));
  };

  const acceptedFileItemsWithDel = useMemo(
      () =>
          files.map((file, i) => (
              <React.Fragment key={file.path || `${file.name}-${file.size}`}>
                {i !== 0 && <FileDelimiter />}
                <FileItemContainer>
                  <FileItemIcon />
                  <FilePath>{file.path || file.name}</FilePath>
                  <FileSize>
                    <FormatSize sizeInBytes={file.size} />
                  </FileSize>
                  <DeleteIcon onClick={() => handleDelete(file)}>Delete</DeleteIcon>
                </FileItemContainer>
              </React.Fragment>
          )),
      [files]
  );

  return (
      <div
          style={{
            width: "100%",
            maxWidth: 600,
            margin: "0 auto",
          }}
      >
        <section>
          <DropContainer
              {...getRootProps({
                className: "dropzone",
                isDragActive,
                isDragAccept,
                isDragReject,
                onClick: handleBrowseClick,
              })}
              data-testid={dropzoneTestId}
              data-tour="dropzone"
          >
            {/* getInputProps() is still rendered so react-dropzone's drag-drop validation
                stays wired up, but it's hidden from the OS picker — we drive picking via
                handleBrowseClick. The fallback input is what gets clicked on browsers
                without showOpenFilePicker support (Safari, Firefox). */}
            <input {...getInputProps()} />
            <input
              type="file"
              hidden
              multiple
              ref={fallbackInputRef}
              onChange={handleFallbackChange}
              data-testid={inputTestId}
            />
            <DropTitleText>Add your assets</DropTitleText>
            <UploadIconBlock>
              <UploadIcon />
            </UploadIconBlock>
            <DropNormalText>
              Drag & drop your files or <DropHighlightedText>browse files</DropHighlightedText>
            </DropNormalText>
          </DropContainer>

          {localError && <DropErrorText>{localError}</DropErrorText>}
          {rejectedExtError && <DropErrorText>{rejectedExtError}</DropErrorText>}

          <div style={{ padding: 5 }} />

          {files?.length > 0 && (
            <>
              <DropTotalSizeText>
                Total package size:<strong>{FormatSize(totalSize)}</strong>
              </DropTotalSizeText>
              <FileList data-tour="upload-file-list">{acceptedFileItemsWithDel}</FileList>
            </>
          )}

          <div style={{ padding: 10 }} />
        </section>
      </div>
  );
};
